const TARGET_RATE = 16000;
const CHUNK = 1280;

export type MicFrame = {
  pcm: Int16Array;
  /** RMS level 0..1 for UI reactivity */
  level: number;
};

/**
 * Capture mic audio, resample to 16 kHz, and emit 80 ms PCM chunks.
 */
export class MicrophoneCapture {
  private stream: MediaStream | null = null;
  private context: AudioContext | null = null;
  private processor: ScriptProcessorNode | null = null;
  private source: MediaStreamAudioSourceNode | null = null;
  private leftover = new Float32Array(0);
  private running = false;
  private onFrame: ((frame: MicFrame) => void) | null = null;

  async start(onFrame: (frame: MicFrame) => void) {
    if (this.running) return;
    this.onFrame = onFrame;

    this.stream = await navigator.mediaDevices.getUserMedia({
      audio: {
        channelCount: 1,
        // Keep raw mic audio: noise suppression distorts speech under
        // background noise, and none of the training audio used it.
        // Wake mic is released while the agent talks, so EC isn't needed.
        echoCancellation: false,
        noiseSuppression: false,
        autoGainControl: false,
      },
      video: false,
    });

    // Prefer a native 16 kHz context so the browser resamples with a proper
    // low-pass filter. Firefox may reject a mismatched rate — fall back.
    try {
      this.context = new AudioContext({ sampleRate: TARGET_RATE });
    } catch {
      this.context = new AudioContext();
    }
    this.source = this.context.createMediaStreamSource(this.stream);

    // ScriptProcessor is deprecated but widely supported and simpler than
    // AudioWorklet for a demo (no separate worklet module fetch/CORS).
    const bufferSize = 4096;
    this.processor = this.context.createScriptProcessor(bufferSize, 1, 1);
    this.processor.onaudioprocess = (event) => {
      if (!this.running || !this.context) return;
      const input = event.inputBuffer.getChannelData(0);
      this.ingest(input, this.context.sampleRate);
    };

    // Mute the graph so we don't play mic audio through the speakers.
    const mute = this.context.createGain();
    mute.gain.value = 0;
    this.source.connect(this.processor);
    this.processor.connect(mute);
    mute.connect(this.context.destination);
    this.running = true;
  }

  stop() {
    this.running = false;
    this.processor?.disconnect();
    this.source?.disconnect();
    this.processor = null;
    this.source = null;
    void this.context?.close();
    this.context = null;
    this.stream?.getTracks().forEach((t) => t.stop());
    this.stream = null;
    this.leftover = new Float32Array(0);
  }

  get isRunning() {
    return this.running;
  }

  private ingest(samples: Float32Array, sampleRate: number) {
    const resampled = resample(samples, sampleRate, TARGET_RATE);
    const merged = new Float32Array(this.leftover.length + resampled.length);
    merged.set(this.leftover, 0);
    merged.set(resampled, this.leftover.length);

    let offset = 0;
    while (offset + CHUNK <= merged.length) {
      const slice = merged.subarray(offset, offset + CHUNK);
      const pcm = floatToInt16(slice);
      const level = rms(slice);
      this.onFrame?.({ pcm, level });
      offset += CHUNK;
    }
    this.leftover = merged.slice(offset);
  }
}

function resample(
  input: Float32Array,
  fromRate: number,
  toRate: number,
): Float32Array {
  if (fromRate === toRate) return input;
  const ratio = fromRate / toRate;
  const outLength = Math.floor(input.length / ratio);
  const output = new Float32Array(outLength);
  for (let i = 0; i < outLength; i++) {
    const src = i * ratio;
    const idx = Math.floor(src);
    const frac = src - idx;
    const a = input[idx] ?? 0;
    const b = input[idx + 1] ?? a;
    output[i] = a + (b - a) * frac;
  }
  return output;
}

function floatToInt16(input: Float32Array): Int16Array {
  const out = new Int16Array(input.length);
  for (let i = 0; i < input.length; i++) {
    const s = Math.max(-1, Math.min(1, input[i]));
    out[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
  }
  return out;
}

function rms(input: Float32Array): number {
  let sum = 0;
  for (let i = 0; i < input.length; i++) sum += input[i] * input[i];
  return Math.sqrt(sum / Math.max(1, input.length));
}
